import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'

import { isBackendConfigured } from '@/app/config'
import {
  AuthError,
  loadAuthProvider,
  type AuthEvent,
  type AuthProvider,
  type AuthUser,
  type SignInResult,
} from '@/auth'
import { createSafeStorage } from '@/utils/safeStorage'

export type AuthStatus = 'signed_out' | 'challenge' | 'signed_in'
/** Why the last session ended without this tab's own sign-out. */
export type SessionEnd = '' | 'expired' | 'signed_out_elsewhere'

/** Device storage written for a signed-in owner; cleared when a different owner signs in. */
const OWNER_STORAGE_PREFIXES = ['ls:prompt:']
const LAST_OWNER_KEY = 'ls:last-owner'

function clearOwnerDeviceState(nextOwner: string): void {
  const storage = createSafeStorage()
  if (storage.get<string>(LAST_OWNER_KEY) === nextOwner) return
  for (const prefix of OWNER_STORAGE_PREFIXES) {
    for (const key of storage.keys(prefix)) storage.remove(key)
  }
  storage.set(LAST_OWNER_KEY, nextOwner)
}

/** Owner authentication state (participants never sign in). */
export const useAuthStore = defineStore('auth', () => {
  const user = shallowRef<AuthUser | null>(null)
  const status = ref<AuthStatus>('signed_out')
  const ready = ref(false)
  /** Increments whenever a session starts, changes account, or refreshes its tokens. */
  const sessionVersion = ref(0)
  const sessionEnd = ref<SessionEnd>('')
  /** Address awaiting the invitation's new-password step. */
  const challengeEmail = ref('')
  const persistent = ref(true)
  const restoreError = ref('')
  let provider: AuthProvider | null = null
  let initPromise: Promise<void> | null = null
  let pendingToken: Promise<string | null> | null = null
  let signingOut = false

  const isSignedIn = computed(() => status.value === 'signed_in')
  const email = computed(() => user.value?.email ?? '')

  function setUser(next: AuthUser | null, ended: SessionEnd = '') {
    if (next) clearOwnerDeviceState(next.id)
    user.value = next
    status.value = next ? 'signed_in' : 'signed_out'
    sessionEnd.value = next ? '' : ended
    if (next) challengeEmail.value = ''
    sessionVersion.value++
  }

  function handle(event: AuthEvent) {
    switch (event.type) {
      case 'signed_in':
        if (user.value?.id === event.user.id) sessionVersion.value++
        else setUser(event.user)
        break
      case 'token_refreshed':
        if (isSignedIn.value) sessionVersion.value++
        break
      case 'signed_out':
        if (isSignedIn.value) setUser(null, signingOut ? '' : 'signed_out_elsewhere')
        break
      case 'session_expired':
        if (isSignedIn.value) setUser(null, 'expired')
        break
    }
  }

  async function requireProvider(): Promise<AuthProvider> {
    await init()
    if (!provider) throw new AuthError('not_configured', 'Owner sign-in is not configured.')
    return provider
  }

  function init(): Promise<void> {
    if (!isBackendConfigured) {
      ready.value = true
      return Promise.resolve()
    }
    initPromise ??= (async () => {
      try {
        provider = await loadAuthProvider()
        provider.subscribe(handle)
        const restored = await provider.restore()
        if (restored) setUser(restored)
      } catch (error) {
        // Stored tokens stay in place, so a later reload can still restore the session.
        restoreError.value =
          error instanceof AuthError && error.code === 'network'
            ? 'Could not reach the sign-in service to restore your session.'
            : 'Could not restore your saved sign-in.'
      } finally {
        persistent.value = provider?.persistent ?? true
        ready.value = true
      }
    })()
    return initPromise
  }

  async function signIn(emailAddress: string, password: string): Promise<SignInResult['step']> {
    const auth = await requireProvider()
    restoreError.value = ''
    const result = await auth.signIn(emailAddress, password)
    persistent.value = auth.persistent
    if (result.step === 'done') setUser(result.user)
    else if (result.step === 'new_password_required') {
      status.value = 'challenge'
      challengeEmail.value = emailAddress.trim()
    }
    return result.step
  }

  async function completeNewPassword(password: string): Promise<void> {
    const auth = await requireProvider()
    try {
      setUser(await auth.completeNewPassword(password))
    } catch (error) {
      if (error instanceof AuthError && error.code === 'no_challenge') cancelChallenge()
      throw error
    }
  }

  function cancelChallenge() {
    if (status.value === 'challenge') status.value = 'signed_out'
    challengeEmail.value = ''
  }

  async function signOut(): Promise<void> {
    const auth = await requireProvider()
    signingOut = true
    try {
      await auth.signOut()
    } finally {
      signingOut = false
    }
    if (isSignedIn.value) setUser(null)
  }

  /**
   * Current access token, refreshed when needed. Concurrent callers share one request. Null
   * means the session is over, and the store reflects that.
   */
  async function accessToken(options: { forceRefresh?: boolean } = {}): Promise<string | null> {
    const auth = await requireProvider()
    const run = async () => {
      try {
        const token = await auth.accessToken(options)
        if (!token) handle({ type: 'session_expired' })
        return token
      } catch (error) {
        if (error instanceof AuthError && error.code === 'session_expired') {
          handle({ type: 'session_expired' })
          return null
        }
        throw error
      }
    }
    if (options.forceRefresh) return run()
    pendingToken ??= run().finally(() => (pendingToken = null))
    return pendingToken
  }

  async function requestPasswordReset(emailAddress: string): Promise<void> {
    await (await requireProvider()).requestPasswordReset(emailAddress)
  }

  async function confirmPasswordReset(
    emailAddress: string,
    code: string,
    newPassword: string,
  ): Promise<void> {
    await (await requireProvider()).confirmPasswordReset(emailAddress, code, newPassword)
  }

  async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await (await requireProvider()).changePassword(currentPassword, newPassword)
  }

  return {
    user,
    email,
    status,
    ready,
    isSignedIn,
    sessionVersion,
    sessionEnd,
    challengeEmail,
    persistent,
    restoreError,
    init,
    signIn,
    completeNewPassword,
    cancelChallenge,
    signOut,
    accessToken,
    requestPasswordReset,
    confirmPasswordReset,
    changePassword,
  }
})
